import React, { useState, useEffect } from "react";
import { useFormContext, get } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import Select from "components/atoms/Select/Select";
import PropTypes from "prop-types";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

/**
 * An input field that can be used inside of a form.
 * @param {*} type The type of input field: text or dropdown
 */
const FormField = function (props) {
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext({ mode: "all" });
  const {
    additional_information,
    label,
    mandatory,
    type,
    field_id,
    placeholder,
    dropdown_values,
    onlyNumber,
    roundOff,
    onChange,
    id,
    paymentLocation,
    defaultValue,
    disabled,
    validationPattern,
    validations,
    eventContext: _eventContext,
    eventName: _eventName,
    containerClasses,
    onKeyPress,
    onBlur: onBlurProp,
    testId,
    ...rest
  } = props;

  // properties that all fields contain
  const properties = {
    type,
    placeholder,
    className: "form-control pt-10 pb-10 pl-14 pr-14 bg-white w-100 height-auto placeholder-light",
    onChange,
  };

  const [timer, setTimer] = useState(null);

  useEffect(() => {
    if (paymentLocation) setValue("payment_locations", paymentLocation);

    if (!getValues(field_id) && defaultValue) setValue(field_id, defaultValue);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function roundOffInput(field_id, value) {
    if (value && roundOff) setValue(field_id, parseFloat(value).toFixed(2).toString());
    else if (value) setValue(field_id, Math.trunc(parseFloat(value).toFixed(2)).toString());
  }

  function checkOnlyNumber(field_id, value) {
    clearTimeout(timer);

    const newTimer = setTimeout(() => {
      roundOffInput(field_id, getValues(field_id));
    }, 1000);

    if (isNaN(value)) setValue(field_id, value.replace(/\D/g, ""));
    else setValue(field_id, value);

    setTimer(newTimer);
  }

  if (onlyNumber) {
    return (
      <div
        className="d-flex flex-column"
        id={field_id}
        data-testid={testId || `form-field-${field_id.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`}
      >
        {label ? (
          <label className={`${mandatory ? "required" : ""} d-flex`}>
            {label}
            <span>
              {additional_information && (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <span className="font-weight-normal">{additional_information}</span>
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faQuestionCircle} className="font-12 text-primary ml-2" />
                </OverlayTrigger>
              )}
            </span>
          </label>
        ) : null}
        <input
          data-testid={
            testId ? `${testId}-input` : `form-field-input-${field_id.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`
          }
          type="text"
          id={id}
          {...properties}
          className={`form-control font-b3 pt-10 pb-10 pl-14 pr-14 bg-white br-10 w-100 height-auto placeholder-light ${
            errors?.[field_id] ? "invalid-input" : ""
          } `}
          defaultValue={getValues(field_id)}
          onChange={(e) => checkOnlyNumber(field_id, e.target.value)}
          disabled={disabled}
          {...register(field_id, { required: mandatory })}
          {...rest}
        />
        {errors?.[field_id] ? (
          <div className="d-block font-b3 invalid-feedback">
            <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
          </div>
        ) : null}
      </div>
    );
  }

  const fieldRegistration =
    type === "dropdown"
      ? null
      : register(field_id, {
          required: mandatory,
          ...validations,
          ...(validationPattern && { pattern: validationPattern }),
        });

  return (
    <div
      className={`d-flex flex-column ${containerClasses}`}
      id={field_id}
      data-testid={testId || `form-field-${field_id.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`}
    >
      {label ? (
        <label className={`${mandatory ? "required" : ""} d-flex`}>
          {label}
          <span>
            {additional_information && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    <span className="font-weight-normal">{additional_information}</span>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="font-12 text-primary ml-2" />
              </OverlayTrigger>
            )}
          </span>
        </label>
      ) : null}
      {type === "dropdown" ? (
        <Select
          data-testid={
            testId ? `${testId}-select` : `form-field-select-${field_id.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`
          }
          isFormElement
          className={`form-control pt-10 pb-10 br-10 pl-14 pr-14 bg-white w-100 height-auto placeholder-light ${
            errors?.[field_id] ? "invalid-input" : ""
          }`}
          name={field_id}
          options={dropdown_values}
          onChange={onChange}
          id={id}
          disabled={disabled}
          required={mandatory}
        />
      ) : (
        <>
          <input
            data-testid={
              testId ? `${testId}-input` : `form-field-input-${field_id.replace(/[.[\]]+/g, "-").replace(/-+$/g, "")}`
            }
            type="text"
            {...properties}
            className={`form-control font-b3 br-10 pt-6 pb-6 pl-10 pr-10 bg-white w-100 height-auto placeholder-light ${
              get(errors, field_id) ? "invalid-input" : ""
            }`}
            defaultValue={getValues(field_id)}
            id={id}
            disabled={disabled}
            {...fieldRegistration}
            {...rest}
            onKeyPress={onKeyPress}
            onBlur={(event) => {
              fieldRegistration.onBlur(event);
              onBlurProp?.(event);
            }}
          />
          {get(errors, field_id) ? (
            <div className="d-block font-b3 invalid-feedback">
              {get(errors, field_id)?.message ? (
                get(errors, field_id).message
              ) : (
                <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

FormField.propTypes = {
  id: PropTypes.string,
  mandatory: PropTypes.bool,
  type: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  paymentLocation: PropTypes.string,
  field_id: PropTypes.string,
  onlyNumber: PropTypes.bool,
  placeholder: PropTypes.string,
  dropdown_values: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  roundOff: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  validationPattern: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp), PropTypes.object]),
  defaultValue: PropTypes.string,
  additional_information: PropTypes.string,
  validations: PropTypes.instanceOf(Object),
  eventContext: PropTypes.string,
  eventName: PropTypes.string,
  containerClasses: PropTypes.string,
  onKeyPress: PropTypes.func,
  onBlur: PropTypes.func,
  testId: PropTypes.string,
};

FormField.defaultProps = {
  additional_information: "",
  type: "text",
  id: "",
  mandatory: false,
  label: "",
  field_id: "",
  placeholder: "",
  paymentLocation: "",
  dropdown_values: [],
  onlyNumber: false,
  roundOff: false,
  onChange: null,
  disabled: false,
  validationPattern: "",
  defaultValue: "",
  validations: null,
  eventContext: "Quote Request Form",
  eventName: "",
  containerClasses: "",
  onKeyPress: undefined,
  testId: "",
};

export default FormField;
